import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Helper function to extract error message
const extractErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const firstError = detail[0];
    return firstError?.msg || firstError?.message || defaultMessage;
  } else if (typeof detail === 'object' && detail !== null) {
    return detail.msg || detail.message || defaultMessage;
  } else if (typeof detail === 'string') {
    return detail;
  }
  return error?.message || defaultMessage;
};

/**
 * User Create Modal Component
 * Allows admin to create new users
 */
const UserCreateModal = ({ isOpen, onClose, onUserCreated }) => {
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [cityHallAddress, setCityHallAddress] = useState('');
  const [mayorName, setMayorName] = useState('');
  const [secretaryName, setSecretaryName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [role, setRole] = useState('client');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Generate initial access code when modal opens
  useEffect(() => {
    if (isOpen && !accessCode) {
      generateAccessCode();
    }
  }, [isOpen]);

  const generateAccessCode = async () => {
    try {
      const response = await axios.get(`${API}/admin/generate-access-code`);
      setAccessCode(response.data.access_code);
    } catch (err) {
      console.error('Error generating access code:', err);
      // Fallback to manual generation
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < 12; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      setAccessCode(result);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const userData = {
        email: email.trim(),
        role,
        access_code: accessCode.trim(),
        city_hall_address: cityHallAddress.trim() || null,
        mayor_name: mayorName.trim() || null,
        secretary_name: secretaryName.trim() || null,
        phone_number: phoneNumber.trim() || null
      };

      const response = await axios.post(`${API}/admin/users`, userData);
      
      onUserCreated(response.data);
      
      // Reset form
      setEmail('');
      setAccessCode('');
      setCityHallAddress('');
      setMayorName('');
      setSecretaryName('');
      setPhoneNumber('');
      setRole('client');
      
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, 'Erreur lors de la création de l\'utilisateur'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setAccessCode('');
    setCityHallAddress('');
    setMayorName('');
    setSecretaryName('');
    setPhoneNumber('');
    setRole('client');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50" 
      style={{backgroundColor: 'rgba(0, 0, 0, 0.8)'}}
      data-testid="user-create-modal"
    >
      <div className="rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl" style={{backgroundColor: '#2C3148'}}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold" style={{color: '#FFFFFF'}}>Nouvel utilisateur</h2>
          <button
            onClick={handleClose}
            className="transition-colors duration-200"
            style={{color: '#8A92B2'}}
            onMouseEnter={(e) => e.currentTarget.style.color = '#FF5757'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#8A92B2'}
            data-testid="user-create-close-btn"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-create-form">
          {/* Email - Required */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Email <span style={{color: '#FF5757'}}>*</span>
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{
                backgroundColor: '#242A3D',
                borderColor: '#4A5BFF',
                color: '#FFFFFF'
              }}
              placeholder="user@example.com"
              data-testid="user-create-email"
            />
          </div>

          {/* Access Code - Required */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Code d&apos;accès initial <span style={{color: '#FF5757'}}>*</span>
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                required
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: '#242A3D',
                  borderColor: '#4A5BFF',
                  color: '#FFFFFF'
                }}
                placeholder="Code d'accès"
                data-testid="user-create-access-code"
              />
              <button
                type="button"
                onClick={generateAccessCode}
                className="px-3 py-2 rounded-md focus:outline-none transition-colors duration-200"
                style={{
                  backgroundColor: '#1C2133',
                  color: '#00C2FF'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#242A3D'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1C2133'}
                title="Générer un nouveau code"
                data-testid="user-create-generate-code-btn"
              >
                🔄
              </button>
            </div>
            <p className="text-xs mt-1" style={{color: '#8A92B2'}}>
              L&apos;utilisateur utilisera ce code pour se connecter la première fois
            </p>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Rôle
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{
                backgroundColor: '#242A3D',
                borderColor: '#4A5BFF',
                color: '#FFFFFF'
              }}
              data-testid="user-create-role"
            >
              <option value="client">Client</option>
              <option value="demo">Demo (accès limité)</option>
              <option value="admin">Admin</option>
            </select>
            {role === 'demo' && (
              <p className="text-xs mt-1" style={{color: '#FFB800'}}>
                ⚠️ Le compte demo peut visualiser mais ne peut pas enregistrer de modifications
              </p>
            )}
          </div>

          {/* Demo mode info - hide city hall fields for demo */}
          {role === 'demo' && (
            <div style={{
              backgroundColor: 'rgba(255, 184, 0, 0.1)',
              border: '1px solid rgba(255, 184, 0, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '8px'
            }}>
              <p style={{color: '#FFB800', fontSize: '13px', margin: 0}}>
                ℹ️ Les comptes Demo ont un abonnement illimité et des informations pré-remplies automatiquement.
              </p>
            </div>
          )}

          {/* City Hall Address - Optional (hidden for demo) */}
          {role !== 'demo' && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
                Adresse de la mairie (optionnel)
              </label>
              <input
                type="text"
                value={cityHallAddress}
                onChange={(e) => setCityHallAddress(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: '#242A3D',
                  borderColor: '#4A5BFF',
                  color: '#FFFFFF'
                }}
                placeholder="123 Rue de la Mairie, 75001 Paris"
                data-testid="user-create-city-hall"
              />
            </div>
          )}

          {/* Mayor's Name - Optional (hidden for demo) */}
          {role !== 'demo' && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
                Nom du maire (optionnel)
              </label>
              <input
                type="text"
                value={mayorName}
                onChange={(e) => setMayorName(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: '#242A3D',
                  borderColor: '#4A5BFF',
                  color: '#FFFFFF'
                }}
                placeholder="Jean Dupont"
                data-testid="user-create-mayor"
              />
            </div>
          )}

          {/* Secretary Name - Optional (hidden for demo) */}
          {role !== 'demo' && (
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Nom de la secrétaire de mairie (optionnel)
            </label>
            <input
              type="text"
              value={secretaryName}
              onChange={(e) => setSecretaryName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{
                backgroundColor: '#242A3D',
                borderColor: '#4A5BFF',
                color: '#FFFFFF'
              }}
              placeholder="Marie Martin"
              data-testid="user-create-secretary"
            />
          </div>
          )}

          {/* Phone Number - Optional (hidden for demo) */}
          {role !== 'demo' && (
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Numéro de téléphone (optionnel)
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{
                backgroundColor: '#242A3D',
                borderColor: '#4A5BFF',
                color: '#FFFFFF'
              }}
              placeholder="01 23 45 67 89"
              data-testid="user-create-phone"
            />
          </div>
          )}

          {error && (
            <div 
              className="text-sm p-3 rounded-md" 
              style={{
                color: '#FF5757',
                backgroundColor: 'rgba(255, 87, 87, 0.1)',
                border: '1px solid #FF5757'
              }}
              data-testid="user-create-error"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border rounded-md transition-colors duration-200"
              style={{
                color: '#8A92B2',
                borderColor: '#1C2133'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1C2133';
                e.currentTarget.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#8A92B2';
              }}
              data-testid="user-create-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 rounded-md transition-all duration-300 disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
                color: '#FFFFFF'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
              data-testid="user-create-submit-btn"
            >
              {isLoading ? 'Création...' : 'Créer l\'utilisateur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserCreateModal;
